import { forwardRef, useEffect, useId, useImperativeHandle, useRef, type PointerEvent } from 'react';
import { Eraser } from 'lucide-react';
import { Button } from './Button';

export interface SignaturePadHandle {
  isEmpty: () => boolean;
  /** Image de la signature (data URL PNG). */
  toDataURL: () => string;
  clear: () => void;
}

interface SignaturePadProps {
  label: string;
  /** Signature tracée (`false`) ou effacée (`true`). */
  onEmptyChange?: (empty: boolean) => void;
}

/** Zone de signature au doigt, au stylet ou à la souris, avec « Effacer » (même tracé que l'application des moniteurs). */
export const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(function SignaturePad({ label, onEmptyChange }, ref) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  const labelId = useId();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    // Taille de mise en page (offsetWidth) : une fenêtre qui s'ouvre avec une mise à l'échelle animée
    // fausserait getBoundingClientRect et décalerait le trait.
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#0f172a';
    }
  }, []);

  const clear = () => {
    const canvas = canvasRef.current;
    canvas?.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
    if (dirty.current) onEmptyChange?.(true);
    dirty.current = false;
  };

  useImperativeHandle(ref, () => ({
    isEmpty: () => !dirty.current,
    toDataURL: () => canvasRef.current?.toDataURL('image/png') ?? '',
    clear,
  }));

  const point = (e: PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const start = (e: PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    drawing.current = true;
    const { x, y } = point(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const move = (e: PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = point(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!dirty.current) {
      dirty.current = true;
      onEmptyChange?.(false);
    }
  };
  const end = () => {
    drawing.current = false;
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <span id={labelId} className="text-[12.5px] font-medium text-secondary">
          {label}
        </span>
        <Button variant="ghost" size="sm" icon={Eraser} onClick={clear}>
          Effacer
        </Button>
      </div>
      <canvas
        ref={canvasRef}
        role="img"
        aria-labelledby={labelId}
        className="h-40 w-full touch-none rounded-xl border border-dashed border-line bg-surface-light"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        onPointerCancel={end}
      />
    </div>
  );
});
